import { useCallback, useEffect, useState } from "react";
import { paymentApi } from "../api/paymentApi";
import Message from "../components/Message";

const emptyForm = {
  payment_type: "credit_card",
  card_name: "",
  card_number: "",
  card_expiry: "",
  card_cvv: "",
  account_name: "",
  bank_name: "",
  bsb: "",
  account_number: "",
};

export default function PaymentMethods() {
  const [methods, setMethods] = useState([]);
  const [payments, setPayments] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState("");
  const [notice, setNotice] = useState(null);

  const loadMethods = useCallback(async (type = filter) => {
    try {
      // Keep the list in sync with the selected type filter from the assessment brief.
      const data = await paymentApi.getPaymentMethods(type);
      setMethods(data.payment_methods || []);
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    }
  }, [filter]);

  useEffect(() => {
    loadMethods();
    paymentApi.getPayments().then((data) => setPayments(data.payments || [])).catch(() => {});
  }, [loadMethods]);

  function updateField(event) {
    let value = event.target.value;
    // Format numeric fields on input so the customer sees clean card/bank details.
    if (event.target.name === "card_number") value = value.replace(/\D/g, "").replace(/(\d{4})/g, "$1 ").trim();
    if (event.target.name === "card_expiry") value = value.replace(/\D/g, "").replace(/(\d{2})(\d{2})/, "$1/$2");
    if (event.target.name === "card_cvv") value = value.replace(/\D/g, "").slice(0, 4);
    if (event.target.name === "bsb") value = value.replace(/\D/g, "").slice(0, 6);
    if (event.target.name === "account_number") value = value.replace(/\D/g, "").slice(0, 12);
    setForm((current) => ({ ...current, [event.target.name]: value }));
  }

  function useStripeTestCard() {
    // Stripe's standard test card keeps local and deployed assessment testing repeatable.
    setForm((current) => ({
      ...current,
      payment_type: "credit_card",
      card_name: current.card_name || "Test Customer",
      card_number: "4242 4242 4242 4242",
      card_expiry: "12/34",
      card_cvv: "123",
    }));
  }

  function editMethod(method) {
    setEditingId(method.id);
    setForm({
      ...emptyForm,
      payment_type: method.payment_type,
      card_name: method.holder_name || "",
      card_number: method.last_four ? `4242 4242 4242 ${method.last_four}` : "",
      card_expiry: method.expiry_month && method.expiry_year ? `${method.expiry_month}/${String(method.expiry_year).slice(-2)}` : "",
      account_name: method.holder_name || "",
      bank_name: method.bank_name || "",
      bsb: String(method.bsb || "").replace(/\D/g, ""),
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    try {
      if (editingId) {
        await paymentApi.updatePaymentMethod(editingId, form);
        setNotice({ type: "success", text: "Payment method updated." });
      } else {
        await paymentApi.createPaymentMethod(form);
        setNotice({ type: "success", text: "Payment method saved." });
      }
      setEditingId(null);
      setForm(emptyForm);
      await loadMethods();
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    }
  }

  async function deleteMethod(methodId) {
    if (!window.confirm("Delete this payment method?")) return;
    try {
      // The backend soft-deletes methods so old payment history remains readable.
      await paymentApi.deletePaymentMethod(methodId);
      setNotice({ type: "success", text: "Payment method deleted." });
      await loadMethods();
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    }
  }

  return (
    <div className="container" style={{ padding: "40px 20px" }}>
      <h1 className="page-title">Payment Methods</h1>
      <Message notice={notice} />

      <div className="checkout-content">
        <div className="checkout-form">
          <h2>{editingId ? "Update Payment Method" : "Add Payment Method"}</h2>
          <form onSubmit={handleSubmit}>
            <div className="payment-method">
              <input type="radio" id="pm_card" name="payment_type" value="credit_card" checked={form.payment_type === "credit_card"} onChange={updateField} />
              <label htmlFor="pm_card">Credit Card</label>
            </div>
            <div className="payment-method">
              <input type="radio" id="pm_bank" name="payment_type" value="bank_transfer" checked={form.payment_type === "bank_transfer"} onChange={updateField} />
              <label htmlFor="pm_bank">Bank Transfer</label>
            </div>

            {form.payment_type === "credit_card" && (
              <div className="payment-section">
                <button type="button" className="btn btn-secondary stripe-test-button" onClick={useStripeTestCard}>Use Stripe Test Card</button>
                <div className="form-group">
                  <label htmlFor="card_name">Cardholder Name</label>
                  <input id="card_name" name="card_name" className="form-control" value={form.card_name} onChange={updateField} required />
                </div>
                <div className="form-group">
                  <label htmlFor="card_number">Card Number</label>
                  <input id="card_number" name="card_number" className="form-control" maxLength="23" value={form.card_number} onChange={updateField} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="card_expiry">Expiry Date</label>
                    <input id="card_expiry" name="card_expiry" className="form-control" placeholder="MM/YY" maxLength="5" value={form.card_expiry} onChange={updateField} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="card_cvv">CVV</label>
                    <input id="card_cvv" name="card_cvv" className="form-control" maxLength="4" value={form.card_cvv} onChange={updateField} required />
                  </div>
                </div>
              </div>
            )}

            {form.payment_type === "bank_transfer" && (
              <div className="payment-section">
                <div className="form-group">
                  <label htmlFor="account_name">Account Name</label>
                  <input id="account_name" name="account_name" className="form-control" value={form.account_name} onChange={updateField} required />
                </div>
                <div className="form-group">
                  <label htmlFor="bank_name">Bank Name</label>
                  <input id="bank_name" name="bank_name" className="form-control" value={form.bank_name} onChange={updateField} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="bsb">BSB</label>
                    <input id="bsb" name="bsb" className="form-control" maxLength="6" value={form.bsb} onChange={updateField} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="account_number">Account Number</label>
                    <input id="account_number" name="account_number" className="form-control" value={form.account_number} onChange={updateField} required />
                  </div>
                </div>
              </div>
            )}

            <button type="submit" className="btn btn-primary">{editingId ? "Update Method" : "Save Method"}</button>
            {editingId && <button type="button" className="btn btn-secondary" style={{ marginLeft: 12 }} onClick={() => { setEditingId(null); setForm(emptyForm); }}>Cancel</button>}
          </form>
        </div>

        <div className="order-summary">
          <h2>Saved Methods</h2>
          <div className="form-group">
            <label htmlFor="method_filter">Search by Type</label>
            <select id="method_filter" className="form-control" value={filter} onChange={(event) => { setFilter(event.target.value); loadMethods(event.target.value); }}>
              <option value="">All</option>
              <option value="credit_card">Credit Card</option>
              <option value="bank_transfer">Bank Transfer</option>
            </select>
          </div>
          {methods.map((method) => (
            <div className="summary-item" key={method.id} style={{ alignItems: "center" }}>
              <span>
                {method.payment_type === "credit_card"
                  ? `${method.brand || "Card"} ending ${method.last_four}`
                  : `${method.bank_name} ending ${method.account_last_four}`}
              </span>
              <span>
                <button className="btn btn-sm btn-secondary" onClick={() => editMethod(method)}>Edit</button>
                <button className="btn btn-sm btn-danger" style={{ marginLeft: 8 }} onClick={() => deleteMethod(method.id)}>Delete</button>
              </span>
            </div>
          ))}
          {methods.length === 0 && <p>No saved payment methods.</p>}
        </div>
      </div>

      <div className="orders-list" style={{ marginTop: 32 }}>
        <h2>Payment History</h2>
        {payments.map((payment) => (
          <div className="order-card" key={payment.id}>
            <div className="order-header">
              <h3>{payment.order_number}</h3>
              <span className={`status-badge status-${payment.payment_status}`}>{String(payment.payment_status).toUpperCase()}</span>
            </div>
            <p><strong>Amount:</strong> ${Number(payment.amount).toFixed(2)}</p>
            <p><strong>Method:</strong> {payment.payment_type}</p>
            <p><strong>Date:</strong> {new Date(payment.payment_date).toLocaleString()}</p>
          </div>
        ))}
        {payments.length === 0 && <p>No payment history yet.</p>}
      </div>
    </div>
  );
}
